import { Suspense, useState } from "react";

import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";

import { DialogLoadErrorBoundary } from "components/dialog-load-error-boundary/dialog-load-error-boundary.component";
import type { EmailBrandingPreviewRequest } from "services/types/email-branding.type";
import { lazyWithRetry } from "utils/lazy-with-retry.util";

import { EMAIL_BRANDING_SETTINGS_URL } from "./email-branding.utils";
import { useEmailBranding } from "./use-email-branding.hook";

const EmailBrandingPreviewDialog = lazyWithRetry(
  () => import("./email-branding-preview-dialog.component"),
  "email-branding-preview-dialog"
);

export interface EmailBrandingLinksProps {
  /** Omitted: no Preview link. Resolves to null when there is nothing to preview yet. */
  getPreviewRequest?: () =>
    | EmailBrandingPreviewRequest
    | null
    | Promise<EmailBrandingPreviewRequest | null>;
  /** Paint the preview above a host dialog that raised its own z-index. */
  zIndex?: number;
}

/** "Preview · Edit email branding". The edit link opens a new tab, so a draft in this one survives. */
export default function EmailBrandingLinks({
  getPreviewRequest,
  zIndex
}: EmailBrandingLinksProps) {
  const { canEdit } = useEmailBranding();
  const [previewRequest, setPreviewRequest] =
    useState<EmailBrandingPreviewRequest | null>(null);

  if (!getPreviewRequest && !canEdit) return null;

  return (
    <Stack direction="row" alignItems="center" gap={0.5} component="span">
      {getPreviewRequest && (
        <Button
          size="small"
          variant="text"
          onClick={async () => setPreviewRequest(await getPreviewRequest())}
        >
          Preview
        </Button>
      )}
      {canEdit && (
        <Button
          size="small"
          variant="text"
          href={EMAIL_BRANDING_SETTINGS_URL}
          target="_blank"
          rel="noopener"
        >
          Edit email branding
        </Button>
      )}
      {previewRequest && (
        <DialogLoadErrorBoundary dialogName="email-branding-preview-dialog">
          <Suspense fallback={null}>
            <EmailBrandingPreviewDialog
              request={previewRequest}
              onClose={() => setPreviewRequest(null)}
              zIndex={zIndex !== undefined ? zIndex + 1 : undefined}
            />
          </Suspense>
        </DialogLoadErrorBoundary>
      )}
    </Stack>
  );
}
